import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { locationsApi } from '../api/locations';
import { shelvesApi } from '../api/shelves';
import ShelfGridSetting from '../components/shelf/ShelfGridSetting';
import { readServerMessage } from '../lib/apiError';
import type { ShelfBox } from '../lib/shelfForm';

interface SettingState {
  name: string;
  rows: number;
  cols: number;
  shelves: ShelfBox[];
}

/**
 * 地点设置（`05-ui-design-system.md` §3.2 `/locations/:id/settings`）。
 *
 * 本阶段只做**坐标底板**（行列数）。收款码（`PUT /locations/:id/settings/:key`）、
 * 新订单提示音与提示音量属于收银端设置页（§3.3 `/cashier/settings`，Phase 5），
 * 广告媒体同理；放进来的话会多出一批本阶段无法验收的入口。
 */
export default function LocationSettingsPage() {
  const { id = '' } = useParams<{ id: string }>();
  const [state, setState] = useState<SettingState | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (): Promise<void> => {
    if (id === '') return;
    setLoading(true);
    setError(null);
    try {
      const [location, shelves] = await Promise.all([
        locationsApi.get(id),
        shelvesApi.listShelves(id),
      ]);
      setState({
        name: location.name,
        rows: location.shelfRows,
        cols: location.shelfCols,
        shelves: shelves.map((shelf) => ({
          shelfId: shelf.shelfId,
          code: shelf.code,
          rowStart: shelf.rowStart,
          colStart: shelf.colStart,
          rowSpan: shelf.rowSpan,
          colSpan: shelf.colSpan,
        })),
      });
    } catch (e) {
      setError(readServerMessage(e) ?? '加载失败，请重试');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  if (id === '') {
    return <p className="font-bold text-red-600">请先选择地点</p>;
  }

  return (
    <div className="flex flex-col gap-4">
      <h1 className="text-2xl font-bold text-gray-900">地点设置{state ? ` · ${state.name}` : ''}</h1>

      {loading && <p className="py-8 text-center text-gray-500">加载中…</p>}
      {!loading && error && <p className="font-bold text-red-600">{error}</p>}

      {!loading && state && (
        <ShelfGridSetting
          locationId={id}
          rows={state.rows}
          cols={state.cols}
          shelves={state.shelves}
          onSaved={({ rows, cols }) => setState({ ...state, rows, cols })}
        />
      )}
    </div>
  );
}
